/**
 * @file contract.ts
 * @author Rapoo Device SDK Team
 * @date 2026-06-22
 * @description 键盘灯光功能契约（领域驱动版）
 *
 * 新通用 API：通过 LightingDomainType 统一驱动所有灯效领域
 * - getConfig/setConfig: 读写完整配置
 * - setProperty/getProperty: 读写单个属性
 * - setKeysColor/getKeysColor/getKeysColorStream: 逐键颜色
 * - createCustomEffect/deleteCustomEffect: 自定义灯效管理
 */

import { RGB, RGBGroup } from '../../types/color'
import {
  GcomeApKeyMatrixEntry,
  keyboardLightingCustomConf,
  KeyboardLightingEffect,
  keyMapLighting,
  keyRgbItem,
  type AuxLightingEnableRegister,
} from '../../types/keyboardLighting'
import type { KeyboardLightingTarget } from '../../types/lighting'
import { LightingConfigData, LightingDomainType } from '../../types/lightingDomain'
import { FeatureBase } from '../FeatureBase'

// 扩展功能注册表
declare module '../FeatureMap' {
  export interface DeviceFeatureMap {
    keyboardLighting: IKeyboardLightingFeature
  }
}

export interface IKeyboardLightingFeature {
  // ==================== 元数据 ====================
  /** 获取设备支持的所有灯效领域类型 */
  getSupportedDomains(): LightingDomainType[]

  /** 获取所有灯效 */
  getEffects(target?: KeyboardLightingTarget): Promise<KeyboardLightingEffect[]>

  // ==================== 设备级操作 ====================
  /** 设置/获取当前灯效（键盘背光） */
  setCurrentEffect(effect: number): Promise<void>
  getCurrentEffect(): Promise<number>

  // 背光开关（0=关, 1=开）
  setBacklightSwitch(enabled: boolean): Promise<void>
  getBacklightSwitch(): Promise<number>

  // 设置/获取背光全局亮度
  setBrightness(brightness: number): Promise<void>
  getBrightness(): Promise<number>

  // 设置/获取全局速度
  setSpeed(speed: number): Promise<void>
  getSpeed(): Promise<number>

  /** 设置/获取侧灯模式（侧灯有独立地址，与键盘背光不同） */
  setSideEffect?(effect: number): Promise<void>
  getSideEffect?(): Promise<number>

  // ==================== 领域通用 CRUD ====================

  /** 读取指定领域的完整配置 */
  getConfig(domain: LightingDomainType, index: number): Promise<LightingConfigData>

  /** 写入指定领域的完整配置 */
  setConfig(
    domain: LightingDomainType,
    index: number,
    config: Partial<LightingConfigData>,
  ): Promise<void>

  /** 设置单个属性 */
  setProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
    value: number | boolean | RGB | RGBGroup,
  ): Promise<void>

  /** 获取单个属性 */
  getProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
  ): Promise<number | boolean | RGB | RGBGroup>

  // ==================== 领域特有操作 ====================
  /** 逐键颜色设置 */
  setKeysColor(domain: LightingDomainType, keyMap: keyMapLighting, index: number): Promise<void[]>

  /** 逐键颜色读取 */
  getKeysColor(domain: LightingDomainType, index: number): Promise<keyMapLighting>

  /** 流式逐键颜色读取 */
  getKeysColorStream(domain: LightingDomainType, index: number): AsyncIterable<keyRgbItem>

  /** 创建自定义灯效 */
  createCustomEffect(config: keyboardLightingCustomConf, index: number): Promise<void>

  /** 删除自定义灯效 */
  deleteCustomEffect(index: number): Promise<number>

  // ==================== 设备级特有操作 ====================
  /** 设置/获取侧灯开关（设备级操作，不归入 domain） */
  setSideLightDynamicEnable?(enable: boolean): Promise<void>
  getSideLightDynamicEnable?(): Promise<boolean>

  /** 读取辅助灯区使能寄存器（充电灯/氛围灯/侧灯共用） */
  getAuxLightingEnableRegister?(): Promise<AuxLightingEnableRegister>

  /** 写入辅助灯区使能寄存器（充电灯/氛围灯/侧灯共用），只更新传入字段 */
  setAuxLightingEnableRegister?(reg: Partial<AuxLightingEnableRegister>): Promise<void>

  /** 读取指示灯配置（index 0=充电灯, index 1=氛围灯） */
  getIndicatorConfig?(index: number): Promise<LightingConfigData>

  /** 写入指示灯配置（index 0=充电灯, index 1=氛围灯） */
  setIndicatorConfig?(index: number, config: Partial<LightingConfigData>): Promise<void>

  /** 读取默认按键矩阵（GCOME_AP 特有，CMD 0x07） */
  readDefaultKeyMatrix?(): Promise<GcomeApKeyMatrixEntry[]>
}

export class KeyboardLightingFeature extends FeatureBase implements IKeyboardLightingFeature {
  getSupportedDomains(): LightingDomainType[] {
    return this.unsupported('KeyboardLightingFeature.getSupportedDomains')
  }

  getEffects(_target?: KeyboardLightingTarget): Promise<KeyboardLightingEffect[]> {
    return this.unsupported('KeyboardLightingFeature.getEffects')
  }

  setCurrentEffect(_effect: number): Promise<void> {
    return this.unsupported('KeyboardLightingFeature.setCurrentEffect')
  }

  getCurrentEffect(): Promise<number> {
    return this.unsupported('KeyboardLightingFeature.getCurrentEffect')
  }

  setBacklightSwitch(_enabled: boolean): Promise<void> {
    return this.unsupported('KeyboardLightingFeature.setBacklightSwitch')
  }
  getBacklightSwitch(): Promise<number> {
    return this.unsupported('KeyboardLightingFeature.getBacklightSwitch')
  }

  setBrightness(_brightness: number): Promise<void> {
    return this.unsupported('KeyboardLightingFeature.setBrightness')
  }
  getBrightness(): Promise<number> {
    return this.unsupported('KeyboardLightingFeature.getBrightness')
  }
  setSpeed(_speed: number): Promise<void> {
    return this.unsupported('KeyboardLightingFeature.setSpeed')
  }
  getSpeed(): Promise<number> {
    return this.unsupported('KeyboardLightingFeature.getSpeed')
  }

  getConfig(_domain: LightingDomainType, _index: number): Promise<LightingConfigData> {
    return this.unsupported('KeyboardLightingFeature.getConfig')
  }

  setConfig(
    _domain: LightingDomainType,
    _index: number,
    _config: Partial<LightingConfigData>,
  ): Promise<void> {
    return this.unsupported('KeyboardLightingFeature.setConfig')
  }

  setProperty(
    _domain: LightingDomainType,
    _index: number,
    _key: string,
    _value: number | boolean | RGB | RGBGroup,
  ): Promise<void> {
    return this.unsupported('KeyboardLightingFeature.setProperty')
  }

  getProperty(
    _domain: LightingDomainType,
    _index: number,
    _key: string,
  ): Promise<number | boolean | RGB | RGBGroup> {
    return this.unsupported('KeyboardLightingFeature.getProperty')
  }

  setKeysColor(
    _domain: LightingDomainType,
    _keyMap: keyMapLighting,
    _index: number,
  ): Promise<void[]> {
    return this.unsupported('KeyboardLightingFeature.setKeysColor')
  }

  getKeysColor(_domain: LightingDomainType, _index: number): Promise<keyMapLighting> {
    return this.unsupported('KeyboardLightingFeature.getKeysColor')
  }

  getKeysColorStream(_domain: LightingDomainType, _index: number): AsyncIterable<keyRgbItem> {
    return this.unsupported('KeyboardLightingFeature.getKeysColorStream')
  }

  createCustomEffect(_config: keyboardLightingCustomConf, _index: number): Promise<void> {
    return this.unsupported('KeyboardLightingFeature.createCustomEffect')
  }

  deleteCustomEffect(_index: number): Promise<number> {
    return this.unsupported('KeyboardLightingFeature.deleteCustomEffect')
  }
}
